import React from "react";
import { EyeOffIcon } from "@components/Common/SvgIcons/EyeOffIcon";
import { EyeIcon } from "@components/Common/SvgIcons/EyeIcon";
import { useTranslation } from "react-i18next";
import { SketchPicker } from "react-color";
import {
    Card,
    SectionTitle,
    LanguageTabs,
    LanguageTab,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    VisibilityButton,
    ColorPickerButton,
    ColorPickerPopover,
    ColorPickerCover,
    ResetButton,
    ResetAllButton,
} from "./styled";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import { ICON_SIZE_SMALL } from "../shared/constants";

type TitleDescriptionProps = {
    mainColor: string;
    selectedLanguage: string;
    onLanguageChange: (lang: string) => void;
    languages: Array<{ code: string; label: string }>;
    defaultTranslations: Record<string, { title: string; description: string }>;
    customTexts: Record<string, { title: string; description: string }>;
    onCustomTitleChange: (value: string) => void;
    onCustomDescriptionChange: (value: string) => void;
    titleVisible: boolean;
    onTitleVisibleChange: () => void;
    descriptionVisible: boolean;
    onDescriptionVisibleChange: () => void;
    titleColor: string;
    onTitleColorChange: (color: string) => void;
    descriptionColor: string;
    onDescriptionColorChange: (color: string) => void;
    onResetTitle: () => void;
    onResetDescription: () => void;
    onResetAll: () => void;
    onResetAllLanguages: () => void;
};

export function TitleDescription({
    mainColor,
    selectedLanguage,
    onLanguageChange,
    languages,
    defaultTranslations,
    customTexts,
    onCustomTitleChange,
    onCustomDescriptionChange,
    titleVisible,
    onTitleVisibleChange,
    descriptionVisible,
    onDescriptionVisibleChange,
    titleColor,
    onTitleColorChange,
    descriptionColor,
    onDescriptionColorChange,
    onResetTitle,
    onResetDescription,
    onResetAll,
    onResetAllLanguages,
}: TitleDescriptionProps): JSX.Element {
    const { t } = useTranslation();

    // Color picker UI state (local to this component)
    const [showTitleColorPicker, setShowTitleColorPicker] = React.useState(
        false
    );
    const [
        showDescriptionColorPicker,
        setShowDescriptionColorPicker,
    ] = React.useState(false);
    const [titlePickerPosition, setTitlePickerPosition] = React.useState({
        top: 0,
        left: 0,
    });
    const [descPickerPosition, setDescPickerPosition] = React.useState({
        top: 0,
        left: 0,
    });
    const titleColorButtonRef = React.useRef<HTMLButtonElement>(null);
    const descColorButtonRef = React.useRef<HTMLButtonElement>(null);

    return (
        <Card data-testid="scan-to-banner-title-description-section">
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "15px",
                }}
            >
                <SectionTitle style={{ marginBottom: 0 }}>
                    {t("Title and Description")}
                </SectionTitle>
                <ResetAllButton
                    type="button"
                    data-testid="scan-to-banner-reset-all-languages"
                    mainColor={mainColor}
                    onClick={onResetAllLanguages}
                    title={t("Reset")}
                    aria-label={t("Reset")}
                >
                    <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                </ResetAllButton>
            </div>

            <LanguageTabs data-testid="scan-to-banner-language-tabs">
                {languages.map((lang) => (
                    <LanguageTab
                        data-testid={`scan-to-banner-language-tab-${lang.code}`}
                        key={lang.code}
                        isActive={selectedLanguage === lang.code}
                        mainColor={mainColor}
                        onClick={() => onLanguageChange(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: "10px" }}>
                <InputRow
                    style={{
                        borderBottom: "1px solid #e0e0e0",
                        paddingBottom: "10px",
                        marginBottom: "10px",
                    }}
                >
                    <InputLabel></InputLabel>
                    <InputLabel style={{ fontWeight: 500 }}>
                        {t("Default text")}
                    </InputLabel>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                        }}
                    >
                        <InputLabel style={{ fontWeight: 500 }}>
                            {t("Customized")}
                        </InputLabel>
                        <ResetAllButton
                            data-testid="scan-to-banner-reset-all"
                            mainColor={mainColor}
                            onClick={onResetAll}
                            title={t("Reset")}
                            aria-label={t("Reset")}
                        >
                            <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                        </ResetAllButton>
                    </div>
                </InputRow>
                <InputRow data-testid="scan-to-banner-title-row">
                    <InputLabel>{t("Title")}</InputLabel>
                    <Input
                        data-testid="scan-to-banner-default-title-input"
                        value={defaultTranslations[selectedLanguage].title}
                        readOnly
                        style={{
                            backgroundColor: "#f8f9fa",
                            cursor: "not-allowed",
                        }}
                    />
                    <InputWrapper>
                        <Input
                            data-testid="scan-to-banner-custom-title-input"
                            value={customTexts[selectedLanguage].title}
                            onChange={(e) =>
                                onCustomTitleChange(e.target.value)
                            }
                            placeholder={
                                defaultTranslations[selectedLanguage].title
                            }
                        />
                        <VisibilityButton
                            data-testid="scan-to-banner-title-visibility-toggle"
                            isVisible={titleVisible}
                            onClick={onTitleVisibleChange}
                            title={
                                titleVisible
                                    ? "Masquer le titre"
                                    : "Afficher le titre"
                            }
                            aria-label={titleVisible ? t("Hide") : t("Show")}
                            aria-pressed={titleVisible}
                        >
                            {titleVisible ? <EyeIcon /> : <EyeOffIcon />}
                        </VisibilityButton>
                        <ColorPickerButton
                            data-testid="scan-to-banner-title-color-picker"
                            ref={titleColorButtonRef}
                            color={titleColor}
                            onClick={(e) => {
                                const rect = (e.target as HTMLElement).getBoundingClientRect();
                                const pickerHeight = 260; // SketchPicker height + padding
                                const spaceBelow =
                                    window.innerHeight - rect.bottom;
                                const shouldPositionAbove =
                                    spaceBelow < pickerHeight;

                                setTitlePickerPosition({
                                    top: shouldPositionAbove
                                        ? rect.top +
                                          window.scrollY -
                                          pickerHeight
                                        : rect.bottom + window.scrollY,
                                    left: rect.left + window.scrollX,
                                });
                                setShowTitleColorPicker(!showTitleColorPicker);
                            }}
                            title={t("Change title color")}
                        />
                        {showTitleColorPicker && (
                            <>
                                <ColorPickerCover
                                    data-testid="scan-to-banner-title-color-picker-cover"
                                    onClick={() =>
                                        setShowTitleColorPicker(false)
                                    }
                                />
                                <ColorPickerPopover
                                    data-testid="scan-to-banner-title-color-picker-popover"
                                    style={{
                                        top: titlePickerPosition.top,
                                        left: titlePickerPosition.left,
                                    }}
                                >
                                    <SketchPicker
                                        color={titleColor}
                                        onChange={(color) =>
                                            onTitleColorChange(color.hex)
                                        }
                                    />
                                </ColorPickerPopover>
                            </>
                        )}
                        <ResetButton
                            data-testid="scan-to-banner-reset-title"
                            mainColor={mainColor}
                            onClick={onResetTitle}
                            title={t("Reset")}
                        >
                            <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                        </ResetButton>
                    </InputWrapper>
                </InputRow>
                <InputRow data-testid="scan-to-banner-description-row">
                    <InputLabel>{t("Description")}</InputLabel>
                    <Input
                        data-testid="scan-to-banner-default-description-input"
                        value={
                            defaultTranslations[selectedLanguage].description
                        }
                        readOnly
                        style={{
                            backgroundColor: "#f8f9fa",
                            cursor: "not-allowed",
                        }}
                    />
                    <InputWrapper>
                        <Input
                            data-testid="scan-to-banner-custom-description-input"
                            value={customTexts[selectedLanguage].description}
                            onChange={(e) =>
                                onCustomDescriptionChange(e.target.value)
                            }
                            placeholder={
                                defaultTranslations[selectedLanguage]
                                    .description
                            }
                        />
                        <VisibilityButton
                            data-testid="scan-to-banner-description-visibility-toggle"
                            isVisible={descriptionVisible}
                            onClick={onDescriptionVisibleChange}
                            title={
                                descriptionVisible
                                    ? "Masquer la description"
                                    : "Afficher la description"
                            }
                            aria-label={
                                descriptionVisible ? t("Hide") : t("Show")
                            }
                            aria-pressed={descriptionVisible}
                        >
                            {descriptionVisible ? <EyeIcon /> : <EyeOffIcon />}
                        </VisibilityButton>
                        <ColorPickerButton
                            data-testid="scan-to-banner-description-color-picker"
                            ref={descColorButtonRef}
                            color={descriptionColor}
                            onClick={(e) => {
                                const rect = (e.target as HTMLElement).getBoundingClientRect();
                                const pickerHeight = 260; // SketchPicker height + padding
                                const spaceBelow =
                                    window.innerHeight - rect.bottom;
                                const shouldPositionAbove =
                                    spaceBelow < pickerHeight;

                                setDescPickerPosition({
                                    top: shouldPositionAbove
                                        ? rect.top +
                                          window.scrollY -
                                          pickerHeight
                                        : rect.bottom + window.scrollY,
                                    left: rect.left + window.scrollX,
                                });
                                setShowDescriptionColorPicker(
                                    !showDescriptionColorPicker
                                );
                            }}
                            title={t("Change description color")}
                        />
                        {showDescriptionColorPicker && (
                            <>
                                <ColorPickerCover
                                    data-testid="scan-to-banner-description-color-picker-cover"
                                    onClick={() =>
                                        setShowDescriptionColorPicker(false)
                                    }
                                />
                                <ColorPickerPopover
                                    data-testid="scan-to-banner-description-color-picker-popover"
                                    style={{
                                        top: descPickerPosition.top,
                                        left: descPickerPosition.left,
                                    }}
                                >
                                    <SketchPicker
                                        color={descriptionColor}
                                        onChange={(color) =>
                                            onDescriptionColorChange(color.hex)
                                        }
                                    />
                                </ColorPickerPopover>
                            </>
                        )}
                        <ResetButton
                            data-testid="scan-to-banner-reset-description"
                            mainColor={mainColor}
                            onClick={onResetDescription}
                            title={t("Reset")}
                        >
                            <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                        </ResetButton>
                    </InputWrapper>
                </InputRow>
            </div>
        </Card>
    );
}
